/* Google Maps view for /open-houses.
 *
 * Pin colors:
 *   - red `#C8102E`  (brand accent — default)
 *   - purple `#7C3AED` (selected for route)
 *   - green `#10B981` (visited)
 *   - gray `#94A3B8` (visited + on route)
 *
 * The popup (InfoWindow) exposes "Add to Route" / "Remove from Route" so
 * selection propagates back to the parent's route-builder modal.
 */
import React, { useCallback, useMemo, useRef, useState } from "react";
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  MarkerClusterer,
  InfoWindow,
  DirectionsRenderer,
  Rectangle,
} from "@react-google-maps/api";
import { Button } from "@/components/ui/button";
import {
  Plus,
  Minus,
  ExternalLink,
  CheckCircle2,
  AlertTriangle,
  BoxSelect,
} from "lucide-react";

const SASKATOON_CENTER = { lat: 52.1332, lng: -106.67 };
// Libraries are loaded as a CONSTANT array reference so useJsApiLoader doesn't
// reload on every render (per @react-google-maps/api docs).
const LIBRARIES = ["places"];

const MAP_CONTAINER_STYLE = { width: "100%", height: "100%" };
const MAP_OPTIONS = {
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: true,
  zoomControl: true,
  gestureHandling: "greedy", // single-finger pan on mobile
  clickableIcons: false,
};

// Google's standard marker images are recolored via path SVG. We use the
// inline SVG-icon approach (no external sprite) which lets us match the
// app's brand palette exactly.
function pinSymbol(color) {
  // window.google may not be loaded yet — caller checks isLoaded first.
  return {
    path: window.google.maps.SymbolPath.CIRCLE,
    scale: 9,
    fillColor: color,
    fillOpacity: 1,
    strokeWeight: 2.5,
    strokeColor: "#ffffff",
  };
}

function teardropPin(color) {
  // SVG path borrowed from Google Maps documentation for a stable teardrop.
  return {
    path: "M 0,0 C -2,-20 -10,-22 -10,-30 a 10,10 0 1,1 20,0 c 0,8 -8,10 -10,30 z",
    fillColor: color,
    fillOpacity: 1,
    strokeColor: "#ffffff",
    strokeWeight: 2,
    scale: 1.4,
    anchor: new window.google.maps.Point(0, 0),
  };
}

export default function OpenHouseMap({
  openHouses,
  selectedIds,
  onToggleSelection,
  onAreaSelect,
  homeCoords,
  onOpenDetail,
  directions,
}) {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
  const { isLoaded, loadError } = useJsApiLoader({
    id: "gmaps-script",
    googleMapsApiKey: apiKey,
    libraries: LIBRARIES,
  });

  const [activeId, setActiveId] = useState(null);
  const [lassoMode, setLassoMode] = useState(false);
  const [drawStart, setDrawStart] = useState(null);
  const [drawEnd, setDrawEnd] = useState(null);
  const mapRef = useRef(null);
  const boundsFitDoneRef = useRef(false);

  const points = useMemo(
    () =>
      (openHouses || []).filter(
        (oh) =>
          typeof oh.lat === "number" &&
          typeof oh.lng === "number" &&
          !Number.isNaN(oh.lat) &&
          !Number.isNaN(oh.lng),
      ),
    [openHouses],
  );

  const noGeo = (openHouses || []).length - points.length;
  const defaultCenter = homeCoords || SASKATOON_CENTER;

  // Fit bounds whenever the filtered set or home pin changes — but only
  // re-fit when the SET ITSELF changes (length + first/last id signature),
  // not every render, so manual pan/zoom isn't constantly overridden.
  const fitSig = useMemo(() => {
    if (!points.length) return "empty";
    return `${points.length}:${points[0]?.id}:${points[points.length - 1]?.id}:${homeCoords ? `${homeCoords.lat.toFixed(3)},${homeCoords.lng.toFixed(3)}` : "nohome"}`;
  }, [points, homeCoords]);

  const onLoadMap = useCallback(
    (map) => {
      mapRef.current = map;
      // initial bounds fit on first load
      doFitBounds(map);
      boundsFitDoneRef.current = true;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fitSig],
  );

  // Re-fit when fitSig changes (after initial load)
  React.useEffect(() => {
    if (!mapRef.current || !boundsFitDoneRef.current) return;
    doFitBounds(mapRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitSig]);

  function doFitBounds(map) {
    if (!window.google) return;
    if (points.length === 0) {
      map.setCenter(defaultCenter);
      map.setZoom(12);
      return;
    }
    const bounds = new window.google.maps.LatLngBounds();
    for (const p of points) bounds.extend({ lat: p.lat, lng: p.lng });
    if (homeCoords) bounds.extend(homeCoords);
    map.fitBounds(bounds, 64);
    // Cap zoom on single-point case
    if (points.length === 1) map.setZoom(14);
  }

  // Handle manual rectangle selection via mouse events
  const onMapMouseDown = useCallback(
    (e) => {
      if (!lassoMode || !e.latLng) return;
      setDrawStart({ lat: e.latLng.lat(), lng: e.latLng.lng() });
      setDrawEnd({ lat: e.latLng.lat(), lng: e.latLng.lng() });
    },
    [lassoMode],
  );

  const onMapMouseMove = useCallback(
    (e) => {
      if (!lassoMode || !drawStart || !e.latLng) return;
      setDrawEnd({ lat: e.latLng.lat(), lng: e.latLng.lng() });
    },
    [lassoMode, drawStart],
  );

  const onMapMouseUp = useCallback(
    (e) => {
      if (!lassoMode || !drawStart || !onAreaSelect) {
        setDrawStart(null);
        setDrawEnd(null);
        return;
      }
      const end = e.latLng ? { lat: e.latLng.lat(), lng: e.latLng.lng() } : drawEnd;
      if (!end) {
        setDrawStart(null);
        setDrawEnd(null);
        return;
      }
      const north = Math.max(drawStart.lat, end.lat);
      const south = Math.min(drawStart.lat, end.lat);
      const east = Math.max(drawStart.lng, end.lng);
      const west = Math.min(drawStart.lng, end.lng);
      const idsInBounds = points
        .filter((p) => p.lat >= south && p.lat <= north && p.lng >= west && p.lng <= east)
        .map((p) => p.id);
      if (idsInBounds.length > 0) {
        onAreaSelect(idsInBounds);
      }
      setDrawStart(null);
      setDrawEnd(null);
      // Keep lassoMode active so the user can draw multiple selections
      // without re-clicking the button each time. They toggle it off manually.
    },
    [lassoMode, drawStart, drawEnd, points, onAreaSelect],
  );

  const drawBounds = useMemo(() => {
    if (!drawStart || !drawEnd) return null;
    return {
      north: Math.max(drawStart.lat, drawEnd.lat),
      south: Math.min(drawStart.lat, drawEnd.lat),
      east: Math.max(drawStart.lng, drawEnd.lng),
      west: Math.min(drawStart.lng, drawEnd.lng),
    };
  }, [drawStart, drawEnd]);

  if (loadError) {
    return (
      <section
        className="rounded-lg border border-red-200 bg-red-50 p-6"
        data-testid="openhouse-map-load-error"
      >
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
          <div>
            <div className="text-sm font-semibold text-red-900">
              Google Maps failed to load
            </div>
            <p className="text-xs text-red-700 mt-1">
              {String(loadError.message || loadError)}. Check that the
              REACT_APP_GOOGLE_MAPS_API_KEY env var is set and that the key
              is allowed for this domain in Google Cloud Console.
            </p>
          </div>
        </div>
      </section>
    );
  }

  if (!apiKey) {
    return (
      <section
        className="rounded-lg border border-amber-200 bg-amber-50 p-6"
        data-testid="openhouse-map-no-key"
      >
        <div className="text-sm font-semibold text-amber-900">
          Map view unavailable
        </div>
        <p className="text-xs text-amber-700 mt-1">
          Set REACT_APP_GOOGLE_MAPS_API_KEY in /app/frontend/.env to enable
          the map.
        </p>
      </section>
    );
  }

  return (
    <section
      className="rounded-lg border border-slate-200 overflow-hidden bg-white"
      data-testid="openhouse-map-view"
    >
      <div className="px-4 py-2 border-b border-slate-200 bg-slate-50 flex flex-wrap items-center justify-between gap-2 text-xs">
        <span className="text-slate-600">
          <span className="font-semibold text-slate-900 tabular-nums">
            {points.length}
          </span>{" "}
          mapped ·{" "}
          <span className="text-emerald-700 font-medium">
            {selectedIds.size}
          </span>{" "}
          on route
          {noGeo > 0 ? (
            <span className="ml-2 text-amber-700">
              · {noGeo} need geocoding
            </span>
          ) : null}
        </span>
        <div className="flex items-center gap-2">
          {onAreaSelect ? (
            <Button
              variant={lassoMode ? "default" : "outline"}
              size="sm"
              onClick={() => setLassoMode((v) => !v)}
              className={`h-7 text-[11px] px-2 ${lassoMode ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""}`}
              data-testid="openhouse-map-lasso-toggle"
            >
              <BoxSelect className="h-3.5 w-3.5 mr-1" />
              {lassoMode ? "Drawing… click & drag" : "Area select"}
            </Button>
          ) : null}
          <LegendBar />
        </div>
      </div>
      <div
        className="relative w-full"
        style={{ height: "60vh", minHeight: "440px", maxHeight: "640px" }}
        data-testid="openhouse-map-container"
      >
        {!isLoaded ? (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-slate-500">
            Loading map…
          </div>
        ) : (
          <GoogleMap
            mapContainerStyle={MAP_CONTAINER_STYLE}
            center={defaultCenter}
            zoom={12}
            onLoad={onLoadMap}
            options={{
              ...MAP_OPTIONS,
              draggable: !lassoMode,
              draggableCursor: lassoMode ? "crosshair" : undefined,
            }}
            onMouseDown={onMapMouseDown}
            onMouseMove={onMapMouseMove}
            onMouseUp={onMapMouseUp}
          >
            {/* Visual rectangle while drawing */}
            {drawBounds ? (
              <Rectangle
                bounds={drawBounds}
                options={{
                  fillColor: "#10B981",
                  fillOpacity: 0.15,
                  strokeColor: "#10B981",
                  strokeWeight: 2,
                  clickable: false,
                }}
              />
            ) : null}

            {/* Home pin */}
            {homeCoords ? (
              <Marker
                position={homeCoords}
                icon={pinSymbol("#0EA5E9")}
                title="Your start point"
                zIndex={9999}
              />
            ) : null}

            {/* Optimized route polyline. Drawn on top of cluster markers but
                we hide the renderer's own markers (suppressMarkers) so our
                pins remain the visible interactive layer. */}
            {directions ? (
              <DirectionsRenderer
                directions={directions}
                options={{
                  suppressMarkers: true,
                  suppressInfoWindows: true,
                  preserveViewport: true,
                  polylineOptions: {
                    strokeColor: "#10B981",
                    strokeOpacity: 0.85,
                    strokeWeight: 5,
                  },
                }}
              />
            ) : null}

            <MarkerClusterer
              options={{
                gridSize: 50,
                maxZoom: 13,
                styles: clusterStyles(),
              }}
            >
              {(clusterer) =>
                points.map((oh) => {
                  const isSelected = selectedIds.has(oh.id);
                  const color = isSelected
                    ? "#7C3AED"
                    : oh.visited
                    ? "#10B981"
                    : "#C8102E";
                  return (
                    <Marker
                      key={oh.id}
                      position={{ lat: oh.lat, lng: oh.lng }}
                      clusterer={clusterer}
                      icon={teardropPin(color)}
                      onClick={() => setActiveId(oh.id)}
                      data-testid={`map-pin-${oh.id}`}
                    />
                  );
                })
              }
            </MarkerClusterer>

            {/* InfoWindow for the active pin */}
            {activeId
              ? (() => {
                  const oh = points.find((p) => p.id === activeId);
                  if (!oh) return null;
                  const isSelected = selectedIds.has(oh.id);
                  return (
                    <InfoWindow
                      position={{ lat: oh.lat, lng: oh.lng }}
                      onCloseClick={() => setActiveId(null)}
                      options={{ pixelOffset: new window.google.maps.Size(0, -28) }}
                    >
                      <PinPopup
                        oh={oh}
                        isSelected={isSelected}
                        onToggleSelection={() => onToggleSelection(oh.id)}
                        onOpenDetail={() => {
                          setActiveId(null);
                          onOpenDetail(oh.id);
                        }}
                      />
                    </InfoWindow>
                  );
                })()
              : null}
          </GoogleMap>
        )}
      </div>
    </section>
  );
}

function PinPopup({ oh, isSelected, onToggleSelection, onOpenDetail }) {
  const events = Array.isArray(oh.events) ? oh.events : [];
  return (
    <div
      className="text-xs leading-snug"
      style={{ minWidth: 220, maxWidth: 280 }}
      data-testid={`map-popup-${oh.id}`}
    >
      <div className="font-semibold text-slate-900 text-sm">
        {oh.address_raw || "—"}
      </div>
      <div className="text-slate-500 mt-0.5">
        {oh.neighborhood ? <span>{oh.neighborhood}</span> : null}
        {oh.price_display ? (
          <span className="ml-2 text-emerald-700 font-medium">
            {oh.price_display}
          </span>
        ) : null}
      </div>

      {events.length > 0 ? (
        <ul className="mt-2 space-y-0.5">
          {events.slice(0, 4).map((ev, idx) => (
            <li key={idx} className="text-slate-700 tabular-nums">
              {formatPopupEvent(ev)}
            </li>
          ))}
        </ul>
      ) : (
        <div className="mt-2 text-slate-500 italic">No published schedule</div>
      )}

      {oh.realtor_name ? (
        <div className="mt-2 text-slate-700">
          <span className="text-slate-500">Realtor:</span>{" "}
          <span className="font-medium">{oh.realtor_name}</span>
        </div>
      ) : null}

      <div className="mt-3 flex flex-wrap gap-2">
        <Button
          size="sm"
          onClick={onToggleSelection}
          className="h-8 px-2 text-xs text-white"
          style={{ background: isSelected ? "#7C3AED" : "hsl(var(--brand))" }}
          data-testid={`map-toggle-route-${oh.id}`}
        >
          {isSelected ? (
            <>
              <Minus className="h-3 w-3 mr-1" /> Remove
            </>
          ) : (
            <>
              <Plus className="h-3 w-3 mr-1" /> Add to route
            </>
          )}
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={onOpenDetail}
          className="h-8 px-2 text-xs"
          data-testid={`map-open-detail-${oh.id}`}
        >
          Details
        </Button>
        {oh.listing_url ? (
          <a
            href={oh.listing_url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs text-slate-500 hover:underline self-center"
          >
            <ExternalLink className="h-3 w-3" /> Realtor.ca
          </a>
        ) : null}
      </div>

      {oh.visited ? (
        <div className="mt-2 text-[11px] inline-flex items-center gap-1 text-slate-500">
          <CheckCircle2 className="h-3 w-3 text-emerald-600" /> Already visited
        </div>
      ) : null}
    </div>
  );
}

function LegendBar() {
  return (
    <div className="flex items-center gap-3 text-[11px] text-slate-500">
      <span className="inline-flex items-center gap-1">
        <span
          className="h-2.5 w-2.5 rounded-full"
          style={{ background: "#C8102E" }}
        />{" "}
        Available
      </span>
      <span className="inline-flex items-center gap-1">
        <span
          className="h-2.5 w-2.5 rounded-full"
          style={{ background: "#7C3AED" }}
        />{" "}
        On route
      </span>
      <span className="inline-flex items-center gap-1">
        <span
          className="h-2.5 w-2.5 rounded-full"
          style={{ background: "#10B981" }}
        />{" "}
        Visited
      </span>
    </div>
  );
}

function clusterStyles() {
  // Solid-fill clusters matching our brand palette. The cluster shows a
  // count overlaid on a colored circle. Three size tiers map to the
  // default MarkerClusterer thresholds (10, 100, 1000).
  const make = (size, hex) => ({
    textColor: "#ffffff",
    textSize: 13,
    height: size,
    width: size,
    // Inline SVG via data: URI — no extra HTTP requests
    url:
      "data:image/svg+xml;utf-8," +
      encodeURIComponent(
        `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
          <circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 2}" fill="${hex}" stroke="#fff" stroke-width="2"/>
        </svg>`,
      ),
  });
  return [make(36, "#C8102E"), make(44, "#9A0B22"), make(52, "#6A0617")];
}

function formatPopupEvent(ev) {
  if (!ev || !ev.start_at) return "—";
  const start = new Date(ev.start_at);
  const end = ev.end_at ? new Date(ev.end_at) : null;
  const dateStr = start.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
  const startStr = start.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
  if (!end) return `${dateStr} · ${startStr}`;
  const endStr = end.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
  return `${dateStr} · ${startStr} – ${endStr}`;
}
